import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, Trash2, ExternalLink, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, TYPE_META } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { FormFields } from "./fields";
import { resourceSchema, NEW_RESOURCE, RESOURCE_TYPES } from "./schemas";
import VoiceBatch from "./VoiceBatch";

function Editor({ initial, onDone }) {
  const { settings } = useSite();
  const [r, setR] = useState(initial);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/categories", { params: { area: r.area } }).then((c) => setCats(c.data.map((x) => x.name)));
  }, [r.area]);
  const save = async () => {
    if (!r.title.trim()) return toast.error("Please add a title");
    setSaving(true);
    try {
      const body = { ...r, items: (r.items || []).filter(Boolean), steps: (r.steps || []).filter(Boolean), tags: (r.tags || []).filter(Boolean) };
      if (r.id) await api.put(`/admin/resources/${r.id}`, body);
      else await api.post("/admin/resources", body);
      toast.success("Resource saved");
      onDone();
    } catch (e) {
      toast.error(formatErr(e));
      setSaving(false);
    }
  };
  const categories = r.category && !cats.includes(r.category) ? [...cats, r.category] : cats;
  return (
    <div data-testid="resource-editor">
      <div className="mb-6 flex items-center justify-between gap-3">
        <button onClick={onDone} className="btn-ghost -ml-3" data-testid="resource-editor-back"><ArrowLeft size={16} /> All resources</button>
        <button onClick={save} disabled={saving} className="btn-primary !min-h-[44px]" data-testid="resource-editor-save">{saving ? "Saving..." : "Save resource"}</button>
      </div>
      <FormFields schema={resourceSchema(settings.library_areas, categories)} value={r} onChange={setR} prefix="resource" />
      <div className="mt-8 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="resource-editor-save-bottom">Save resource</button>
      </div>
    </div>
  );
}

export default function ResourcesAdmin() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const load = () => api.get("/admin/resources").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  const shown = useMemo(() => list.filter((r) => (!type || r.type === type) && (!q || r.title.toLowerCase().includes(q.toLowerCase()))), [list, q, type]);

  const toggleAccess = async (r) => {
    const next = { ...r, access: r.access === "free" ? "members" : "free" };
    await api.put(`/admin/resources/${r.id}`, next);
    setList(list.map((x) => (x.id === r.id ? next : x)));
    toast.success(`Now ${next.access === "free" ? "Free" : "Members Only"}`);
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await api.delete(`/admin/resources/${r.id}`);
    load();
  };

  if (editing) return <Editor initial={editing} onDone={() => { setEditing(null); load(); }} />;
  return (
    <div data-testid="admin-resources">
      <VoiceBatch onDone={load} />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-bt-sagedeep" size={16} />
          <input className="w-full rounded-xl border border-bt-border bg-white py-3 pl-11 pr-4" placeholder="Search resources" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-resources-search" />
        </div>
        <select className="rounded-xl border border-bt-border bg-white px-4 py-3" value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-resources-type-filter">
          <option value="">All templates</option>
          {RESOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button onClick={() => setEditing(structuredClone(NEW_RESOURCE))} className="btn-primary !min-h-[48px]" data-testid="admin-resources-new"><Plus size={16} /> New resource</button>
      </div>
      <div className="divide-y divide-[color:var(--bt-border)] rounded-2xl border border-bt-border bg-white">
        {shown.map((r) => (
          <div key={r.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-testid={`admin-resource-row-${r.slug}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-bt-heading">{r.title} {!r.published && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Draft</span>}</p>
              <p className="text-sm text-bt-sagedeep">{TYPE_META[r.type] || r.type} · {r.area} · {r.category || "No category"}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => toggleAccess(r)} title="Click to switch" data-testid={`admin-resource-access-${r.slug}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${r.access === "free" ? "bg-bt-sage text-bt-primary" : "bg-bt-primary text-bt-bg"}`}>
                {r.access === "free" ? "Free" : "Members Only"}
              </button>
              <a href={`/r/${r.slug}`} target="_blank" rel="noreferrer" className="rounded-lg p-2 hover:bg-bt-sage" aria-label="View"><ExternalLink size={16} /></a>
              <button onClick={() => setEditing(structuredClone(r))} className="rounded-lg p-2 hover:bg-bt-sage" aria-label="Edit" data-testid={`admin-resource-edit-${r.slug}`}><Pencil size={16} /></button>
              <button onClick={() => remove(r)} className="rounded-lg p-2 text-red-700 hover:bg-red-50" aria-label="Delete" data-testid={`admin-resource-delete-${r.slug}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {!shown.length && <p className="p-6 text-center text-sm">No resources found.</p>}
      </div>
    </div>
  );
}
